/**
 * Stylized mockup of the NIS360 task board (kanban + progress) used as a
 * screenshot stand-in on marketing pages. Texts are intentionally hardcoded
 * German, exactly like a real product screenshot would be.
 */

interface MockupTask {
	title: string;
	priority: "Hoch" | "Mittel" | "Niedrig";
	initials: string;
}

const PRIORITY_STYLES: Record<MockupTask["priority"], string> = {
	Hoch: "bg-accent-tint text-text",
	Mittel: "bg-warning-light text-warning-ink",
	Niedrig: "bg-accent-tint text-accent-strong",
};

const COLUMNS: { name: string; count: number; tasks: MockupTask[] }[] = [
	{
		name: "Offen",
		count: 12,
		tasks: [
			{
				title: "Meldeprozess für Sicherheitsvorfälle definieren",
				priority: "Hoch",
				initials: "TK",
			},
			{ title: "Lieferanten-Risikobewertung durchführen", priority: "Mittel", initials: "SM" },
		],
	},
	{
		name: "In Arbeit",
		count: 6,
		tasks: [
			{ title: "Backup- & Recovery-Konzept dokumentieren", priority: "Hoch", initials: "JR" },
			{ title: "Multi-Faktor-Authentifizierung einführen", priority: "Mittel", initials: "TK" },
		],
	},
	{
		name: "Erledigt",
		count: 14,
		tasks: [
			{ title: "Betroffenheitsanalyse abgeschlossen", priority: "Niedrig", initials: "AM" },
			{ title: "Verantwortlichkeiten festgelegt", priority: "Mittel", initials: "SM" },
		],
	},
];

function TaskCard({ task }: { task: MockupTask }) {
	return (
		<div className="bg-white rounded-lg border border-border p-2.5 flex flex-col gap-2">
			<p className="font-urbanist text-[11px] font-medium text-ink leading-[140%]">{task.title}</p>
			<div className="flex items-center justify-between">
				<span
					className={`font-urbanist text-[9px] font-semibold px-1.5 py-0.5 rounded ${
						PRIORITY_STYLES[task.priority]
					}`}
				>
					{task.priority}
				</span>
				<span className="w-5 h-5 rounded-full bg-ink text-white text-[8px] font-semibold flex items-center justify-center">
					{task.initials}
				</span>
			</div>
		</div>
	);
}

export default function BoardMockup() {
	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-1.5">
				<div className="flex items-center justify-between">
					<p className="font-urbanist text-[13px] font-bold text-ink">Ihr NIS-2-Maßnahmenplan</p>
					<p className="font-urbanist text-[11px] font-semibold text-success">
						14 von 32 Maßnahmen umgesetzt
					</p>
				</div>
				<div className="w-full h-2 bg-border-soft rounded-full overflow-hidden">
					<div className="h-full w-[44%] bg-success rounded-full" />
				</div>
			</div>
			<div className="grid grid-cols-3 gap-2.5">
				{COLUMNS.map((column) => (
					<div key={column.name} className="bg-surface rounded-xl p-2.5 flex flex-col gap-2">
						<div className="flex items-center justify-between px-0.5">
							<p className="font-urbanist text-[11px] font-bold text-ink">{column.name}</p>
							<span className="font-urbanist text-[10px] font-semibold text-subtle bg-white border border-border rounded-full px-1.5">
								{column.count}
							</span>
						</div>
						{column.tasks.map((task) => (
							<TaskCard key={task.title} task={task} />
						))}
					</div>
				))}
			</div>
		</div>
	);
}
